import { getTokenFacilityInfo } from "@risalabs_frontend_org/auth-wrapper";
import { SearchableSelect, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, SpinningLoader, TextInput } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { updateProfile } from "../../../api/postCall/updateProfile";
import DragDropUpload from "../../../components/dragDropUpload/dragDropUpload";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { HealthFacilityField } from "../../../enums/healthFacilityField";
import { AppDispatch, RootState } from "../../../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../../store/UserGeneralInfoStore";
import { logError } from "../../../utils/customLogger";

interface HealthFacility {
  id: string;
  name: string;
  is_external_organization?: boolean;
}

interface InternalOrganization {
  facility_id: string;
  facility_name: string;
}

const profileDetialsInitialStates = {
  firstName: "",
  lastName: "",
  email: "",
  profilePicture: "",
};

const Profile = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const [profileDetails, setProfileDetails] = useState(
    profileDetialsInitialStates,
  );

  const [profileDetailsInitial, setProfileDetailsInitial] = useState(
    profileDetialsInitialStates,
  );

  const [healthFacilities, setHealthFacilities] = useState<HealthFacility[]>(
    [],
  );

  const [internalOrganizations, setInternalOrganizations] = useState<
    InternalOrganization[]
  >([]);

  const [loading, setLoading] = useState(false);
  const [fetchingHealthFacility, setFetchingHealthFacility] = useState(false);
  const [saveButtonDisable, setSaveButtonDisable] = useState(true);

  const fetchHealthFacility = async () => {
    setFetchingHealthFacility(true);
    try {
      const healthFacilityData = (await FirestoreService.getAllDocuments(
        FirestoreCollectionReference.healthcareFacility(),
      )) as HealthFacility[];
      setHealthFacilities(healthFacilityData);
    } catch (error) {
      logError(error as Error, "Error fetching health facility data");
    } finally {
      setFetchingHealthFacility(false);
    }
  };

  const checkIfExternalOrganization = () => {
    const currentFacilityId = UserStore.getItem(
      UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID,
    );
    if (!currentFacilityId || healthFacilities.length === 0) return false;

    const currentFacility = healthFacilities.find(
      (facility) => facility.id === currentFacilityId,
    );

    return currentFacility?.is_external_organization === true;
  };

  const fetchInternalOrganizations = () => {
    const correspondingInternalOrgId =
      singleHealthFacilityDetails?.corresponding_internal_org_id;

    if (!correspondingInternalOrgId || healthFacilities.length === 0) {
      setInternalOrganizations([]);
      return;
    }

    // Find the internal org in the already-fetched facilities array
    const internalOrgFacility = healthFacilities.find(
      (facility) => facility.id === correspondingInternalOrgId,
    );

    if (internalOrgFacility) {
      const internalOrg: InternalOrganization = {
        facility_id: correspondingInternalOrgId,
        facility_name: internalOrgFacility.name || "",
      };
      setInternalOrganizations([internalOrg]);
    } else {
      setInternalOrganizations([]);
    }
  };

  const handleValueChange = (data) => {
    if (data.name === "profile-first-name") {
      setProfileDetails({
        ...profileDetails,
        firstName: data.value,
      });
    } else if (data.name === "profile-last-name") {
      setProfileDetails({
        ...profileDetails,
        lastName: data.value,
      });
    }
  };

  const downloadedFilesURL = (url: string[]) => {
    if (url[0] !== "") {
      setProfileDetails({
        ...profileDetails,
        profilePicture: url[0],
      });
    }
  };

  const updateProfileDetails = async () => {
    setLoading(true);

    try {
      const response = await updateProfile({
        id: providerDetails?.id || "",
        FirstName: profileDetails.firstName || "",
        LastName: profileDetails.lastName,
        ProfilePicUrl: profileDetails.profilePicture || undefined,
      });

      if (response.success) {
        toast.success(ToastMessages.PROFILE_UPDATE_SUCCESS);
      } else {
        toast.error(response.message || ToastMessages.PROFILE_UPDATE_ERROR);
      }
    } catch (error) {
      toast.error(ToastMessages.PROFILE_UPDATE_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const hasValueChanged = (currentProfileDetails) => {
    for (const key in currentProfileDetails) {
      if (currentProfileDetails.hasOwnProperty(key)) {
        if (currentProfileDetails[key] !== profileDetailsInitial[key]) {
          return true;
        }
      }
    }
    return false;
  };

  const updateFacilityId = async (facilityId: string) => {
    setLoading(true);
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.providers(),
        providerDetails?.id || "",
        {
          FacilityId: facilityId,
        },
      );
    } catch (error) {
      logError(error as Error, "Error updating facility id");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providerDetails) {
      setProfileDetails({
        firstName: providerDetails?.FirstName || "",
        lastName: providerDetails?.LastName || "",
        email: providerDetails?.EmailAddresses?.[0] || "",
        profilePicture: providerDetails?.ProfilePicUrl || "",
      });
      setProfileDetailsInitial({
        firstName: providerDetails?.FirstName || "",
        lastName: providerDetails?.LastName || "",
        email: providerDetails?.EmailAddresses?.[0] || "",
        profilePicture: providerDetails?.ProfilePicUrl || "",
      });
    }
  }, [providerDetails, dispatch]);

  useEffect(() => {
    setSaveButtonDisable(!hasValueChanged(profileDetails));
  }, [profileDetails]);

  useEffect(() => {
    fetchHealthFacility();
  }, []);

  useEffect(() => {
    if (healthFacilities.length > 0 && singleHealthFacilityDetails) {
      const isExternal = checkIfExternalOrganization();

      if (isExternal) {
        fetchInternalOrganizations();
      }
    }
  }, [healthFacilities, singleHealthFacilityDetails]);

  // Filter health facilities based on the user's allowed facilities from token
  // Always include both internal and external facilities
  const healthFacilityOptions = useMemo(() => {
    const tokenFacilityInfo = getTokenFacilityInfo();
    const internalFacilityIds = tokenFacilityInfo?.internal_facilities || [];
    const externalFacilityIds = tokenFacilityInfo?.external_facilities || [];

    // Combine both internal and external facility IDs
    const allAllowedFacilityIds = [
      ...internalFacilityIds,
      ...externalFacilityIds,
    ];

    // If no combined facilities, fall back to all_facilities from token
    const allowedFacilityIds =
      allAllowedFacilityIds.length > 0
        ? allAllowedFacilityIds
        : tokenFacilityInfo?.all_facilities || [];

    // Only show facilities that are in the token's allowed list
    const facilitiesToShow = healthFacilities.filter((facility) =>
      allowedFacilityIds.includes(facility[HealthFacilityField.ID]),
    );

    return facilitiesToShow.map((healthFacility) => ({
      id: healthFacility[HealthFacilityField.ID],
      text: healthFacility[HealthFacilityField.NAME],
    }));
  }, [healthFacilities]);

  const getCurrentHealthFacility = () => {
    if (providerDetails?.FacilityId && healthFacilities.length > 0) {
      return healthFacilities.find(
        (facility) =>
          facility[HealthFacilityField.ID] === providerDetails.FacilityId,
      );
    }
    return null;
  };

  const getCurrentInternalOrganization = () => {
    const selectedInternalOrgId = UserStore.getItem(
      UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
    );
    if (selectedInternalOrgId && internalOrganizations.length > 0) {
      return internalOrganizations.find(
        (org) => org.facility_id === selectedInternalOrgId,
      );
    }
    return null;
  };

  const currentHealthFacility = getCurrentHealthFacility();
  const currentInternalOrganization = getCurrentInternalOrganization();

  return (
    <div className="profile--container">
      <div className="profile--container__header">Profile</div>
      <div className="profile--container__body">
        <div className="profile--container__body--firstName">
          <TextInput
            id={"profile-first-name"}
            label={"First Name"}
            defaultValue={profileDetails.firstName}
            onChange={(e) => {
              handleValueChange(e);
            }}
          />
        </div>
        <div className="profile--container__body--lastName">
          <TextInput
            id={"profile-last-name"}
            label={"Last Name"}
            defaultValue={profileDetails.lastName}
            onChange={(e) => {
              handleValueChange(e);
            }}
          />
        </div>
        <div className="profile--container__body--email">
          <TextInput
            id={"profile-email"}
            label={"Email"}
            defaultValue={profileDetails.email}
            readOnly={true}
          />
        </div>
        <div className="flex flex-row items-center gap-1">
          {fetchingHealthFacility ? (
            <div className="flex flex-row items-center gap-1">
              <SpinningLoader />
              Fetching healthcare facilities...
            </div>
          ) : (
            <SearchableSelect
              id="health-facility"
              options={healthFacilityOptions.map((healthFacility) => ({
                value: healthFacility.id,
                label: healthFacility.text,
              }))}
              label="Health Facility"
              placeholder={
                currentHealthFacility
                  ? currentHealthFacility[HealthFacilityField.NAME]
                  : currentInternalOrganization
                    ? currentInternalOrganization.facility_name
                    : "Select Health Facility"
              }
              searchPlaceholder="Search health facilities"
              onOptionChange={(data) => {
                updateFacilityId(data.value);
              }}
            />
          )}
        </div>
        {loading && (
          <div className="mt-1 flex items-center gap-2">
            <SpinningLoader />
            Changing healthcare facility...
          </div>
        )}
        <div className="profile--container__body--profile-pic-container">
          <div className="profile--container__body--profile-pic-container__heading">
            Profile Picture
          </div>
          <div className="profile--container__body--profile-pic-container__content">
            <div className="profile--container__body--profile-pic-container__content--image">
              <img
                src={profileDetails.profilePicture || "/user.png"}
                alt="profile-pic"
                className="profile--container__body--profile-pic-container__content--image__image"
              />
            </div>

            <div className="profile--container__body--profile-pic-container__content--upload">
              <DragDropUpload
                id="profile_picture"
                subText="Supported file format .jpg, .png not exceeding 5MB "
                multipleFilesAllowed={false}
                maxFileSize={5 * 1024 * 1024}
                // fileListLength={}
                downloadedURL={downloadedFilesURL}
                allowedFileTypes={[".jpg", ".png"]}
                onSuccessfullyUploaded={loading}
                filePath={`user/providers/${providerDetails?.id}/profile_picture`}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="profile--container__footer">
        <Button
          disabled={false}
          onClick={() => {}}
          buttonType={"secondary"}
          size={"medium"}
        >
          {" "}
          Cancel
        </Button>
        <Button
          disabled={saveButtonDisable}
          buttonType={"primary"}
          size={"medium"}
          onClick={updateProfileDetails}
        >
          Save
          {loading && <SpinningLoader />}
        </Button>
      </div>
    </div>
  );
};

export default Profile;
